import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface HistoryState {
  items: any[];
  timestamp: number;
}

export const useHistoryStore = defineStore('history', () => {
  const past = ref<HistoryState[]>([]);
  const future = ref<HistoryState[]>([]);
  const maxSize = 50; // 最多保存 50 个历史记录

  // 是否可以撤销
  const canUndo = computed(() => past.value.length > 0);

  // 是否可以重做
  const canRedo = computed(() => future.value.length > 0);

  // 记录当前状态
  function pushState(items: any[]) {
    const state: HistoryState = {
      items: JSON.parse(JSON.stringify(items)), // 深拷贝
      timestamp: Date.now(),
    };

    past.value.push(state);

    // 限制历史记录数量
    if (past.value.length > maxSize) {
      past.value.shift();
    }

    // 清空重做历史
    future.value = [];
  }

  // 撤销
  function undo(currentItems: any[]): any[] | null {
    if (!canUndo.value) return null;

    // 保存当前状态到 future
    const currentState: HistoryState = {
      items: JSON.parse(JSON.stringify(currentItems)),
      timestamp: Date.now(),
    };
    future.value.push(currentState);

    // 恢复上一个状态
    const previousState = past.value.pop();
    return previousState ? JSON.parse(JSON.stringify(previousState.items)) : null;
  }

  // 重做
  function redo(currentItems: any[]): any[] | null {
    if (!canRedo.value) return null;

    // 保存当前状态到 past
    const currentState: HistoryState = {
      items: JSON.parse(JSON.stringify(currentItems)),
      timestamp: Date.now(),
    };
    past.value.push(currentState);

    // 恢复下一个状态
    const nextState = future.value.pop();
    return nextState ? JSON.parse(JSON.stringify(nextState.items)) : null;
  }

  // 清空历史
  function clear() {
    past.value = [];
    future.value = [];
  }

  return {
    past,
    future,
    canUndo,
    canRedo,
    pushState,
    undo,
    redo,
    clear,
  };
});
